/**
 * A contact's phone numbers, as a list a person edits.
 *
 * Free of React and of api.ts, like reviewModel.ts, so the rules are tested without a
 * DOM. The rules are the server's (@signal/contracts and @signal/domain-card), restated
 * here only as far as the screen needs them:
 *
 * - A contact has a LIST of numbers, in the person's order, each with a type. There is no
 *   separate "the phone" field; the primary is simply the first callable number.
 * - Fax is not a way to reach a person, so a fax is never the primary and has no
 *   "Make primary".
 * - A type comes from evidence (a printed label, a CSV column, the number plan) or from
 *   the person. The screen shows the printed label only when it says something the type
 *   does not ("printed: Main" under Office); otherwise provenance is noise.
 * - The server validates every number. A refused one is reported beside the editor and
 *   the person's input is kept exactly as typed.
 */

export const PHONE_TYPES = ['mobile', 'work', 'direct', 'home', 'fax', 'toll_free', 'unknown'] as const;
export type PhoneType = (typeof PHONE_TYPES)[number];

/** The customer's word for each type. `work` is the office line; `unknown` is Other. */
export const PHONE_TYPE_LABEL: Record<PhoneType, string> = {
  mobile: 'Mobile',
  work: 'Office',
  direct: 'Direct',
  home: 'Home',
  fax: 'Fax',
  toll_free: 'Toll-free',
  unknown: 'Other',
};

/** Everything except fax can be called. */
export const isCallable = (t: PhoneType): boolean => t !== 'fax';

export const asPhoneType = (t: string | null | undefined): PhoneType =>
  (PHONE_TYPES as readonly string[]).includes(t ?? '') ? t as PhoneType : 'unknown';

/** What will happen to one number before anything is sent (the server's PhoneDelivery). */
export type PhoneDelivery = 'will_send' | 'already_in_crm' | 'crm_full' | 'kept_not_sent' | 'invalid';
export interface PhoneDeliveryLine { value: string; type: PhoneType; delivery: PhoneDelivery }

/** Said per number, and only when the number will NOT simply be sent. */
export const DELIVERY_TEXT: Record<Exclude<PhoneDelivery, 'will_send'>, string> = {
  already_in_crm: 'Already in your CRM',
  crm_full: 'Won’t be sent — your CRM has no free phone/fax place',
  kept_not_sent: 'Kept in ContactPilot — existing CRM contacts aren’t changed',
  invalid: 'Not a valid number',
};

/** One row of the editor. `key` is stable across edits; everything after `type` is evidence. */
export interface PhoneRow {
  key: string;
  value: string;
  type: PhoneType;
  /** The label printed or written beside it ("Cell", "Main"), when there was one. */
  label?: string | null;
  /** How sure the reading was, for a number read from an image. */
  confidence?: number | null;
  /** False when the source number did not parse. */
  valid?: boolean;
  /** The canonical number the server knows this row by, for matching its delivery lines. */
  e164?: string | null;
  /** The value as it arrived, so "edited by you" can be told apart from "as read". */
  original?: string;
  originalType?: PhoneType;
  /** The reading suggests a fax but nothing printed says so (the server's `possibleFax`). */
  possibleFax?: boolean;
  /** The person chose this row's type themselves, which answers any question about it. */
  typeChosen?: boolean;
}

let seq = 0;
/** A key for a new row. Unique within a page's lifetime, which is all a React key needs. */
export const newKey = (): string => `phone-${++seq}`;

/** Below this, a reading is worth a second look. The same threshold the card fields use. */
export const LOW_CONFIDENCE = 0.7;

const row = (value: string, type: PhoneType, extra: Partial<PhoneRow> = {}): PhoneRow => ({
  key: newKey(), value, type, original: value, originalType: type, ...extra,
});

/** A business card's reading: every number on the card, as the server deduplicated them. */
export function fromDraftPhones(phones: ReadonlyArray<{
  value: string; valid: boolean; type: string; label: string | null; confidence: number | null;
  possibleFax?: boolean;
}> | undefined, legacy?: string | null): PhoneRow[] {
  if (phones) {
    return phones.map((p) => row(p.value, asPhoneType(p.type), {
      label: p.label, confidence: p.confidence, valid: p.valid, e164: p.valid ? p.value : null,
      possibleFax: p.possibleFax === true,
    }));
  }
  // A reading from before the list existed carries one number and no type.
  return legacy ? [row(legacy, 'unknown')] : [];
}

/**
 * What a person already decided for a card: their list when they saved one, or — for a
 * card reviewed before the list existed — their single number, as one Other row. Null
 * when they have decided nothing yet, so the caller falls back to the reading.
 *
 * A saved type is the person's answer, so a resumed row never asks "may be a fax?" again.
 */
export function fromReviewedPhones(reviewed: {
  phones?: ReadonlyArray<{ value: string; type: string }> | null; phone?: string | null;
} | null | undefined): PhoneRow[] | null {
  if (!reviewed) return null;
  if (reviewed.phones) return reviewed.phones.map((p) => row(p.value, asPhoneType(p.type)));
  return reviewed.phone ? [row(reviewed.phone, 'unknown')] : [];
}

/**
 * An email participant's numbers: every one the email carried, each with the type its
 * signature gave it (a labelled fax included), or Other.
 */
export function fromCapturePhones(phones: ReadonlyArray<{ value: string; type?: string | null }> | undefined): PhoneRow[] {
  return (phones ?? []).map((p) => row(p.value, asPhoneType(p.type), { valid: true, e164: p.value }));
}

/** An imported contact's numbers. Shown as written in the file; the server keeps both. */
export function fromCandidatePhones(phones: ReadonlyArray<{
  e164: string | null; raw: string; type?: string; confidence?: number; label?: string | null;
}> | undefined): PhoneRow[] {
  return (phones ?? []).map((p) => row(p.raw || p.e164 || '', asPhoneType(p.type), {
    label: p.label ?? null, confidence: p.confidence ?? null, valid: p.e164 !== null, e164: p.e164,
  }));
}

/** The index of the primary: the first callable row with a number. -1 when there is none. */
export function primaryIndex(rows: readonly PhoneRow[]): number {
  return rows.findIndex((r) => isCallable(r.type) && r.value.trim() !== '');
}

/**
 * Makes a row the primary by moving it to the top. A fax cannot be primary, so asking
 * changes nothing.
 */
export function makePrimary(rows: readonly PhoneRow[], key: string): PhoneRow[] {
  const i = rows.findIndex((r) => r.key === key);
  if (i < 0 || !isCallable(rows[i]!.type)) return [...rows];
  return [rows[i]!, ...rows.slice(0, i), ...rows.slice(i + 1)];
}

export const addRow = (rows: readonly PhoneRow[]): PhoneRow[] =>
  [...rows, { key: newKey(), value: '', type: rows.length === 0 ? 'mobile' : 'unknown' }];
export const removeRow = (rows: readonly PhoneRow[], key: string): PhoneRow[] =>
  rows.filter((r) => r.key !== key);
/** Changes one row. Choosing a type marks it as the person's choice. */
export const updateRow = (rows: readonly PhoneRow[], key: string, patch: Partial<Pick<PhoneRow, 'value' | 'type'>>): PhoneRow[] =>
  rows.map((r) => (r.key === key ? { ...r, ...patch, ...(patch.type !== undefined ? { typeChosen: true } : {}) } : r));

/** At most this many numbers per contact: the server's limit. */
export const MAX_PHONES = 10;

/** The request body: every number with something in it, in order, with its type. */
export function serializePhones(rows: readonly PhoneRow[]): Array<{ value: string; type: PhoneType }> {
  return rows
    .map((r) => ({ value: r.value.trim(), type: r.type }))
    .filter((r) => r.value !== '');
}

/** Whether the list differs from what it started as: a number, a type, or the order. */
export function phonesChanged(rows: readonly PhoneRow[], initial: readonly PhoneRow[]): boolean {
  const a = serializePhones(rows);
  const b = serializePhones(initial);
  return a.length !== b.length || a.some((p, i) => p.value !== b[i]!.value || p.type !== b[i]!.type);
}

/** Edited by the person, as opposed to as read: worth a quiet note, never a badge. */
export const isEdited = (r: PhoneRow): boolean =>
  r.original === undefined || r.value.trim() !== r.original.trim() || r.type !== r.originalType;

const digits = (s: string) => s.replace(/\D/g, '');

/** The server's line for a row, matched by number. Null when the server said nothing about it. */
export function deliveryFor(r: PhoneRow, lines: readonly PhoneDeliveryLine[] | undefined): PhoneDelivery | null {
  if (!lines || r.value.trim() === '') return null;
  const d = digits(r.value);
  const hit = lines.find((l) => l.value === r.value.trim() || (r.e164 && l.value === r.e164)
    || (d.length >= 6 && digits(l.value) === d));
  return hit?.delivery ?? null;
}

/** Words a printed label uses for each type, so "Cell" under Mobile is not repeated back. */
const SAME_AS_TYPE: Record<PhoneType, readonly string[]> = {
  mobile: ['mobile', 'mob', 'm', 'cell', 'cellular', 'cell phone', 'mobile phone', 'handy'],
  work: ['office', 'work', 'w', 'o', 'business', 'office phone', 'work phone'],
  direct: ['direct', 'dir', 'd', 'direct line', 'dd', 'ddi', 'direct dial'],
  home: ['home', 'h', 'home phone'],
  fax: ['fax', 'f', 'facsimile', 'fax number'],
  toll_free: ['toll free', 'toll-free', 'tollfree', 'freephone', 'free phone'],
  unknown: [],
};

/**
 * The printed label, when it tells the person something the type does not. "Main" under
 * Office is worth seeing; "Cell" under Mobile is the same fact twice.
 */
export function printedHint(r: PhoneRow): string | null {
  const raw = (r.label ?? '').trim();
  if (!raw) return null;
  const norm = raw.toLowerCase().replace(/[.:#()]/g, '').replace(/\s+/g, ' ').trim();
  if (norm === '' || SAME_AS_TYPE[r.type].includes(norm)) return null;
  if (norm === PHONE_TYPE_LABEL[r.type].toLowerCase()) return null;
  return `printed: ${raw}`;
}

/** A reading uncertain enough to say so — until the person has touched it. */
export const isUncertain = (r: PhoneRow): boolean =>
  !isEdited(r) && r.value.trim() !== ''
  && (r.valid === false || (r.confidence != null && r.confidence < LOW_CONFIDENCE));

/** The one question a number may raise, and its words. */
export type PhoneHintKind = 'invalid' | 'possible_fax' | 'hard_to_read';
export const PHONE_HINT_TEXT: Record<PhoneHintKind, string> = {
  invalid: 'This number can’t be read — fix or remove it',
  possible_fax: 'This may be a fax number — choose Fax if it is',
  hard_to_read: 'Check this — hard to read',
};

/**
 * What to ask about one number, computed from the row as it is NOW — so the question goes
 * away the moment the person answers it, and never reappears on its own.
 *
 * - invalid: the reading did not parse, and the person has not changed the number yet.
 * - possible_fax: the reading suggests a fax, the row is not typed Fax, and the person has
 *   not chosen a type. Choosing any type (Fax or not) answers it; a resumed row carries
 *   the type the person saved, so it never asks.
 * - hard_to_read: a low-confidence reading nobody has touched.
 */
export function phoneHint(r: PhoneRow): PhoneHintKind | null {
  const value = r.value.trim();
  if (value === '') return null;
  const unchanged = r.original !== undefined && value === r.original.trim();
  if (r.valid === false && unchanged) return 'invalid';
  if (r.possibleFax && r.type !== 'fax' && !r.typeChosen) return 'possible_fax';
  if (!isEdited(r) && r.confidence != null && r.confidence < LOW_CONFIDENCE) return 'hard_to_read';
  return null;
}

/**
 * A card's own warnings, without the ones about a single number: those are asked beside
 * that number (phoneHint), where fixing it makes them disappear. A card read before the
 * server stopped sending them still carries them, so they are filtered here too.
 */
export const cardLevelWarnings = (warnings: readonly string[] | undefined): string[] =>
  (warnings ?? []).filter((w) => !/could not be read as a valid number|may be a fax number/i.test(w));

/** The server's refusal of a number, read from a failed request. Null for any other error. */
export function invalidPhoneError(e: unknown): { message: string; values: string[] } | null {
  const data = (e as { data?: { error?: string; message?: string; phones?: unknown } } | null)?.data;
  if (data?.error !== 'invalid_phone') return null;
  return {
    message: data.message ?? 'One of the phone numbers is not a valid number. Fix or remove it to continue.',
    values: Array.isArray(data.phones) ? data.phones.map(String) : [],
  };
}
